/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */

/**
 * ipProtection.js — single source of truth for the Intellectual Property /
 * Copyright protection layer (watermark, footer notice, EULA gate, anti-tamper).
 *
 * Legal identity comes from `lib/legalOwner.js`; do not duplicate CF/address here.
 */
import { LEGAL_OWNER } from '@/lib/legalOwner';

export const IP_YEAR = 2026;
export const IP_PRODUCT = 'SanitàNexus White-Label';
export const IP_OWNER_TITLE = `Ing. ${LEGAL_OWNER.fullName}`;

/** Repeating low-opacity background text (login, setup wizard, admin area). */
export const IP_WATERMARK_TEXT =
  `Proprietà Esclusiva: ${IP_OWNER_TITLE} - Prototipo SanitàNexus Protetto da L. 633/1941`;

/** Global footer notice, visible to every role. */
export const IP_FOOTER_NOTICE =
  `© ${IP_YEAR} ${IP_PRODUCT} di ${LEGAL_OWNER.fullName}. Tutti i diritti riservati. ` +
  'Piattaforma Registrata ed Espressamente Protetta contro la duplicazione di codice e architettura AI.';

/** EULA shown on first login of the demo (kept in sync with backend ip_protection.py). */
export const EULA_VERSION = '1.0';
export const EULA_TITLE = 'Termini di Riservatezza — Demo SanitàNexus White-Label';
export const EULA_TEXT =
  "Accedendo a questa demo live di SanitàNexus White-Label, l'utente riconosce che l'intera architettura software, " +
  "le logiche Local-First, l'integrazione dell'AI locale (WebLLM/Ollama) e l'interfaccia grafica sono di proprietà " +
  'intellettuale esclusiva di Marco Panzetti. È fatto espresso divieto di copia, riproduzione o imitazione delle ' +
  'funzionalità e delle logiche dei prompt per lo sviluppo di software concorrenti, sotto pena di violazione del ' +
  "diritto d'autore italiano.";
export const EULA_CHECKBOX_LABEL = 'Accetto i Termini di Riservatezza';

/** Console banner printed once at boot (visible to anyone opening DevTools). */
export const IP_CONSOLE_BANNER =
  `%c${IP_PRODUCT}%c\n© ${IP_YEAR} ${LEGAL_OWNER.fullName} (CF ${LEGAL_OWNER.fiscalCode}). Tutti i diritti riservati.\n` +
  'Codice, architettura local-first, layout UI/UX e configurazioni dei prompt sono protetti dalla Legge 633/1941. ' +
  'Copia, reverse engineering o imitazione sono vietati.';

/**
 * Roles for which the anti-tamper protections are ACTIVE.
 * Root Administrator (`is_admin`) is always exempt (full DevTools / right-click / selection).
 */
const PROTECTED_ROLES = new Set(['patient', 'demo', 'guest']);

export const isRootAdmin = (user) => Boolean(user?.is_admin);

export const isDemoAccount = (user) => {
  if (!user) return false;
  if (user.is_demo === true) return true;
  const email = String(user.email || '').toLowerCase();
  return email.startsWith('demo.') || email.startsWith('demo@') || email.startsWith('guest');
};

/**
 * True when the anti-tamper layer must be engaged for the current session:
 *  - unauthenticated visitor (login / welcome / register / legal pages)
 *  - role patient / demo / guest, or any demo-flagged account
 * False for the Root Administrator and (by spec) for verified professionals.
 */
export const isProtectedSession = (user) => {
  if (!user) return true;
  if (isRootAdmin(user)) return false;
  if (isDemoAccount(user)) return true;
  return PROTECTED_ROLES.has(String(user.role || '').toLowerCase());
};

/** The EULA gate applies to every authenticated non-admin user of the demo. */
export const needsEulaGate = (user) => {
  if (!user) return false;
  if (isRootAdmin(user)) return false;
  return !(user.eula_accepted_at && user.eula_version === EULA_VERSION);
};

